import { IconWrench } from '../../components/icons'
import type { components } from '../../lib/api/schema.gen'

/**
 * Cart add-on services section (design: `Fictiv Platform.dc.html` ~L2267,
 * `hasCartServices`) — a purple-wrench card after the line table listing the
 * unpriced, quote-required manufacturing services on the cart.
 *
 * Consumes the server-derived `CartView.requestedServices` projection (UID-314,
 * ADR-0105): the type→title mapping and scope/badge labels are all resolved
 * server-side, so this component renders `title`/`scopeLabel`/`description`/
 * `badgeLabel` verbatim (no FE-side label maps).
 *
 * UID-423 (ADR-0127): `requestedServices` is PERSISTED (in-memory) and
 * `onRemove` drives a real `DELETE /cart/services/{id}` (CartPage.tsx),
 * superseding the earlier "view-only local dismissal" note that used to live
 * here — removal now survives reload within the session. UID-424: services
 * are populated ONLY by upstream processes (BOM/RFQ/other) via the API — the
 * cart screen shows and removes them, but never adds one; there is no add
 * affordance here or anywhere else on this screen.
 */
export type CartServiceItem = components['schemas']['CartRequestedServiceView']

const EMPTY_BUSY_IDS: ReadonlySet<string> = new Set()

export interface CartServicesSectionProps {
  readonly services: readonly CartServiceItem[]
  readonly onRemove?: (id: string) => void
  /** Service ids with a `DELETE /cart/services/{id}` in flight (UID-423,
   * ADR-0127 §F) — disables that row's remove control so a slow request
   * can't be double-submitted, mirroring `CartLineRow`'s `busy` prop for
   * line removal. Defaults to empty so existing callers/tests are
   * unaffected. */
  readonly busyIds?: ReadonlySet<string>
}

export function CartServicesSection({
  services,
  onRemove,
  busyIds = EMPTY_BUSY_IDS,
}: CartServicesSectionProps) {
  if (services.length === 0) return null

  return (
    // `<section>` + heading + `<ul>`/`<li>` so the services group has a
    // navigable heading and list semantics for assistive tech (WCAG 1.3.1),
    // rather than a flat pile of <div>s.
    <section className="card cart-services-card" aria-labelledby="cart-services-heading">
      <div className="cart-services-header">
        <span className="cart-services-header-icon" aria-hidden="true">
          <IconWrench size={16} />
        </span>
        <h2 id="cart-services-heading" className="cart-services-title">
          Services
        </h2>
        <span className="cart-services-count">{services.length} · priced with your quote</span>
      </div>
      <ul className="cart-services-list">
        {services.map((service) => {
          const label = service.title
          const scopeText = `${service.bomName ? `${service.bomName} · ` : ''}applies to ${service.scopeLabel}`
          return (
            <li key={service.id} className="cart-services-row">
              <span className="cart-services-row-icon" aria-hidden="true">
                <IconWrench size={16} />
              </span>
              <div className="cart-services-row-info">
                <p className="cart-services-row-label">{label}</p>
                <p className="cart-services-row-scope" title={scopeText}>
                  {scopeText}
                </p>
                {service.description && (
                  <p className="cart-services-row-desc">“{service.description}”</p>
                )}
              </div>
              <span className="cart-services-badge">{service.badgeLabel ?? 'Quote required'}</span>
              {onRemove && (
                <button
                  type="button"
                  className="cart-services-remove"
                  aria-label={`Remove ${label}`}
                  disabled={busyIds.has(service.id)}
                  onClick={() => onRemove(service.id)}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  </svg>
                </button>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
